import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  IUserDetailSetting,
  PHRTrackerTemperatureDTO,
  TemperatureTypeEnum,
  UserDetailSettingEnum,
} from '../../../../generated';
import { IPHRPatientEnhanced } from '../../../../store/patient/phr/phrReducers';
import { phrTemperaturesSelector } from '../../../../store/patient/phr/phrSelectors';
import { getAllForPhrSection } from '../../../../store/root-creator';
import { ReduxPHRCategoryEnum } from '../../../../store/types';
import { enumToOptions } from '../../../../utils';
import { convertTemperature } from '../../../../utils/conversionHelpers';
import PhrItem, { SecondaryInfoComponent } from '../../components/PhrItem';
import { VitalsTracker } from '../../vitals/components/PatientVitalsCard/usePatientVitalsCard';
import TrackerView from '../TrackerView';
import TemperatureChart from './TemperatureChart';
import TemperatureForm from './TemperatureForm';

interface IProps {
  patient: IPHRPatientEnhanced;
  userDetailSettings: IUserDetailSetting[];
  renderHeader: (addButton?: any, onBack?: () => void, title?: string) => any;
  defaultBack?: any;
  defaultTitle?: string;
  showSingleTrackerForm?: (id: ReduxPHRCategoryEnum, tracker: VitalsTracker) => void;
}

const TemperatureComponent = ({
  patient,
  userDetailSettings,
  renderHeader,
  showSingleTrackerForm,
  defaultBack,
  defaultTitle,
}: IProps) => {
  const dispatch = useDispatch();
  const unitValue = userDetailSettings.filter(
    (x) => x.userDetailSettingEnum === UserDetailSettingEnum.Temperature,
  )[0].value;
  const [selectedUnitValue, setSelectedUnitValue] = useState<TemperatureTypeEnum>(
    TemperatureTypeEnum[unitValue],
  );
  const phrTemperatures = phrTemperaturesSelector(patient.phrTemperatures);
  const [convertedTemperatures, setConvertedTemperatures] = useState<PHRTrackerTemperatureDTO[]>(
    [],
  );

  useEffect(() => {
    if (patient) {
      dispatch(getAllForPhrSection(ReduxPHRCategoryEnum.PHRTemperatures, patient.patientID));
    }
  }, [dispatch, patient]);

  useEffect(() => {
    const converted = JSON.parse(JSON.stringify(phrTemperatures));
    setConvertedTemperatures(
      converted.map((x) => {
        x.temperature = convertTemperature(true, x.temperature, selectedUnitValue);
        return x;
      }),
    );
  }, [selectedUnitValue, phrTemperatures]);

  const thinView = (x: PHRTrackerTemperatureDTO, onView?: (data: any) => void) => (
    <>
      <PhrItem
        name={`${x.temperature} Degrees ${selectedUnitValue}`}
        isVerified={x.verified}
        onView={() => {
          if (onView) {
            onView(x);
          }
        }}
        SecondaryInfo={
          <SecondaryInfoComponent
            startDate={moment(x.trackerDate).toDate()}
            startDateTitle="Created on"
            showTime
          />
        }
      />
    </>
  );

  return (
    <TrackerView
      renderHeader={renderHeader}
      phrCategoryEnum={ReduxPHRCategoryEnum.PHRTemperatures}
      patient={patient}
      trackerData={convertedTemperatures}
      thinView={thinView}
      Chart={TemperatureChart}
      Form={TemperatureForm}
      createText="Temperature"
      unitTypeOptions={enumToOptions(TemperatureTypeEnum)}
      unitValue={selectedUnitValue}
      setSelectedUnitValue={setSelectedUnitValue}
      showSingleTrackerForm={showSingleTrackerForm}
      defaultBack={defaultBack}
      defaultTitle={defaultTitle}
    />
  );
};

export default TemperatureComponent;
